/** What a desk's rationale covers (screens §2.6): the decisions changed since the year opened, each as one line "CD rate: Lag → Follow", and the measures the catalog expects them to move. */
import type { ContentCatalog, DecisionCatalogEntry } from "@bsim/contracts";
import type { DecisionFamily, Decisions, KpiId } from "@bsim/domain";
import { lowerFirst } from "../../format/labels";
import { changeLegs, changeTag, shortLabel } from "../decisionValues";
import { deskLabel } from "../labels";
import type { NextStep } from "../nextStep";

export const changedEntries = (catalog: Pick<ContentCatalog, "decisions">, family: DecisionFamily, decisions: Decisions, seed: Partial<Decisions>): readonly DecisionCatalogEntry[] =>
  catalog.decisions.filter((d) => d.family === family && changeTag(d, decisions, seed) !== null);

/** The most change lines a reason shows before "and N more changes". */
export const MAX_CHANGE_LINES = 4;

/** One line per decision that differs from the round-open value, "before → after" in the labels its controls show; a decision with several parts names each moved part ("Retail checking rate, new money: A → B · existing base: C → D"). A decision changed and changed back has no line. */
export function changeLines(entries: readonly DecisionCatalogEntry[], decisions: Decisions, seed: Partial<Decisions>): readonly string[] {
  return entries.flatMap((e) => {
    const legs = changeLegs(e, decisions, seed);
    if (legs.length === 0) return [];
    const name = shortLabel(e.label);
    return [legs.map((l, i) => (l.leg === null ? `${name}: ${l.change}` : `${i === 0 ? `${name}, ` : ""}${lowerFirst(l.leg)}: ${l.change}`)).join(" · ")];
  });
}

/** The lines to show and what is left over: "and 2 more changes". */
export const capChangeLines = (lines: readonly string[]): { readonly shown: readonly string[]; readonly more: string | null } => {
  const extra = lines.length - MAX_CHANGE_LINES;
  return { shown: lines.slice(0, MAX_CHANGE_LINES), more: extra > 0 ? `and ${extra} more ${extra === 1 ? "change" : "changes"}` : null };
};

/** The changed decisions' own measures (catalog `expectKpis`), each once, in the order they are listed. */
export const coverMeasures = (entries: readonly DecisionCatalogEntry[]): readonly KpiId[] => [...new Set(entries.flatMap((e) => e.expectKpis ?? []))];

/** "Write the Loans reason (Business loan pricing, CRE growth)": the step as the bar and the sheet name it, with what changed in short names; a long list is counted instead. */
export function withCovers(step: NextStep, catalog: ContentCatalog, decisions: Decisions, seed: Partial<Decisions>): NextStep {
  if (step.kind !== "rationale") return step;
  const entries = changedEntries(catalog, step.family, decisions, seed);
  const names = entries.map((e) => shortLabel(e.label)).join(", ");
  const named = names === "" ? "" : names.length <= 70 ? ` (${names})` : ` (${entries.length} changes)`;
  return { ...step, label: `Write the ${deskLabel(catalog, step.family)} reason${named}` };
}
